import { Toast, ToastBody, ToastTitle, useToastController } from '@fluentui/react-components';
import { useCallback, useMemo } from 'react';

export const TOASTER_ID = 'app-toaster';

/**
 * Toast policy, which matters more than the API:
 *
 *  - Toasts report the transient result of an EXPLICIT user action
 *    ("Download started", "Reset link sent").
 *  - Never for query load failures. A message that disappears after four
 *    seconds is not an acceptable way to explain why a list is empty; that
 *    gets an inline <ErrorState>.
 *  - Never for form validation. That belongs on the field, or in the form's
 *    MessageBar for form-level problems.
 *  - Errors are sticky (timeout -1) and assertive; successes auto-dismiss and
 *    are polite.
 */
export function useToaster() {
  const { dispatchToast } = useToastController(TOASTER_ID);

  const notifySuccess = useCallback(
    (title: string, body?: string) => {
      dispatchToast(
        <Toast>
          <ToastTitle>{title}</ToastTitle>
          {body ? <ToastBody>{body}</ToastBody> : null}
        </Toast>,
        { intent: 'success', timeout: 4000, politeness: 'polite' },
      );
    },
    [dispatchToast],
  );

  const notifyError = useCallback(
    (title: string, body?: string) => {
      dispatchToast(
        <Toast>
          <ToastTitle>{title}</ToastTitle>
          {body ? <ToastBody>{body}</ToastBody> : null}
        </Toast>,
        { intent: 'error', timeout: -1, politeness: 'assertive' },
      );
    },
    [dispatchToast],
  );

  return useMemo(() => ({ notifySuccess, notifyError }), [notifySuccess, notifyError]);
}
